import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight } from 'lucide-react';
import { Button } from '../../components/Button.js';

interface HeaderProps {
  onOpenLeadModal: (source: string) => void;
}

export const Header: React.FC<HeaderProps> = ({ onOpenLeadModal }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-md border-b border-slate-200/80 py-3.5 shadow-sm'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Brand Logo */}
        <a href="#" className="flex items-center gap-3 group">
          <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-700/60 flex items-center justify-center p-1 shadow-md shadow-blue-500/10 group-hover:scale-105 transition-transform overflow-hidden">
            <img src="/logo-grid-dark.png" alt="ARAYA Vault Logo" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-xl font-extrabold tracking-wider text-slate-900">ARAYA</span>
              <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">阿頼耶</span>
            </div>
            <p className="text-[10px] tracking-wider text-slate-500 font-mono uppercase">Cryptographic Ledger Vault</p>
          </div>
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
          <a href="#problem" className="hover:text-emerald-600 transition-colors">課題とリスク</a>
          <a href="#solution" className="hover:text-emerald-600 transition-colors">特長・解決策</a>
          <a href="#architecture" className="hover:text-emerald-600 transition-colors">仕組み・技術</a>
          <a href="#matrix" className="hover:text-emerald-600 transition-colors">Google Vault対比表</a>
          <a href="#roi" className="hover:text-emerald-600 transition-colors">削減シミュレーション</a>
          <a href="#faq" className="hover:text-emerald-600 transition-colors">よくある質問</a>
        </nav>

        {/* Header CTA */}
        <div className="hidden md:flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenLeadModal('header_doc')}
          >
            資料ダウンロード
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<ArrowRight className="w-4 h-4" />}
            onClick={() => onOpenLeadModal('header_contact')}
          >
            PoC・デモ相談
          </Button>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="md:hidden p-2 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="メニューを開く"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white/95 border-b border-slate-200 px-6 py-6 space-y-4 backdrop-blur-xl shadow-lg animate-fade-in">
          <div className="flex flex-col space-y-3 text-base font-medium text-slate-700">
            <a href="#problem" onClick={() => setMobileMenuOpen(false)} className="py-2 hover:text-emerald-600">課題とリスク</a>
            <a href="#solution" onClick={() => setMobileMenuOpen(false)} className="py-2 hover:text-emerald-600">特長・解決策</a>
            <a href="#architecture" onClick={() => setMobileMenuOpen(false)} className="py-2 hover:text-emerald-600">仕組み・技術</a>
            <a href="#matrix" onClick={() => setMobileMenuOpen(false)} className="py-2 hover:text-emerald-600">Google Vault対比表</a>
            <a href="#roi" onClick={() => setMobileMenuOpen(false)} className="py-2 hover:text-emerald-600">削減シミュレーション</a>
            <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="py-2 hover:text-emerald-600">よくある質問</a>
          </div>
          <div className="pt-4 border-t border-slate-100 flex flex-col gap-3">
            <Button
              variant="outline"
              size="md"
              className="w-full justify-center"
              onClick={() => {
                setMobileMenuOpen(false);
                onOpenLeadModal('mobile_doc');
              }}
            >
              資料ダウンロード
            </Button>
            <Button
              variant="primary"
              size="md"
              className="w-full justify-center"
              icon={<ArrowRight className="w-4 h-4" />}
              onClick={() => {
                setMobileMenuOpen(false);
                onOpenLeadModal('mobile_contact');
              }}
            >
              PoC・デモ相談
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
